import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Select from "react-dropdown-select";
import _ from "lodash";
import { useForm } from "react-hook-form";
import { useAuthContext } from "context/auth.context";

import {
  useCreateNumberOfQuiz,
  useCreateQuizChapters,
  useGetNumberOfQuiz,
  useGetSingleQuizChapter,
  useUpdateNumberOfQuiz,
  useUpdateQuizChapters,
  useDeleteQuizChapters,
} from "hooks/api/students";
import {
  useGetBoards,
  useGetClasses,
  useGetQuizMChapters,
  useGetSubjects,
} from "hooks/api/subject";
import PageHeader from "components/BreadCrumb";
import FormTextField from "components/FormField/TextField";
import FormSelect from "components/FormField/Select";
import FormTextEditor from "components/FormField/TextEditor";
import ImageFile from "components/FormField/ImageFile";
import { useQueryClient } from "@tanstack/react-query";
import { useToaster } from "hooks/custom/useToaster";

const rules = {
  required: {
    value: true,
    message: "This field is required!",
  },
};

// Add this style for the tags
const tagStyle = {
  display: "inline-block",
  backgroundColor: "#e0e0e0",
  padding: "4px 8px",
  borderRadius: "4px",
  margin: "4px",
  fontSize: "14px",
};

const CreateUpdateQuizChapters = ({
  board_id,
  class_id,
  subject_id,
  setAddUpdate,
  addUpdate,
  setEditId,
  num_of_quiz,
  existingQuizNumbers,
  editId,
  refetchParent,
  type_id,
}) => {
  const { user } = useAuthContext();
  const id = editId;
  const [valueAdded, setValueAdded] = React.useState(false);
  const [render, setRender] = React.useState(id ? false : true);
  const queryClient = useQueryClient();
  const { toasterSuccess, toasterError } = useToaster();
  // The hook will only run when we have an editId
  const { data: quizChapter, refetch } = useGetSingleQuizChapter(editId);
  const { data: chapters } = useGetQuizMChapters(
    board_id,
    class_id,
    subject_id
  );

  useEffect(() => {
    if (editId) {
      refetch();
    }
  }, [editId, refetch]);

  // Update the mutation hooks usage
  const createNumberOfQuiz = useCreateQuizChapters({
    onSuccess: () => {
      reset();
      toasterSuccess("Quiz chapters created successfully!");
      if (refetchParent) refetchParent();
    },
    onError: (err) => {
      toasterError(
        err.response?.data?.message || "Failed to create quiz chapters"
      );
    },
  });

  const updateNumberOfQuiz = useUpdateQuizChapters({
    onSuccess: () => {
      reset();
      toasterSuccess("Quiz chapters updated successfully!");
      if (refetchParent) refetchParent();
    },
    onError: (err) => {
      toasterError(
        err.response?.data?.message || "Failed to update quiz chapters"
      );
    },
  });

  const { handleSubmit, reset, watch, setValue, control, register, formState } =
    useForm();

  async function onSubmit(data) {
    try {
      const chaptersArray = watch("chapters_id") || [];

      const payload = {
        quiz_number: Number(data.quiz_number),
        board_id: Number(board_id),
        class_id: Number(class_id),
        type_id: type_id,
        subject_id: Number(subject_id),
        chapters_id: chaptersArray,
        ...(id && { id: Number(id) }), // Only include id if it exists
      };

      console.log("Final payload:", payload);

      if (id) {
        // Verify id exists before update
        if (!id) {
          throw new Error("Missing ID for update operation");
        }
        await updateNumberOfQuiz.mutateAsync({ ...payload, id: Number(id) });
      } else {
        await createNumberOfQuiz.mutateAsync(payload);
      }
    } catch (err) {
      console.error("Submission error:", err.response?.data || err.message);
    }
  }

  useEffect(() => {
    if (
      !!id &&
      !!render &&
      !!Object.values(watch()).length > 0 &&
      quizChapter?.data?.[0]
    ) {
      setValueAdded(true);
    }

    if (!id && !!render) {
      setValueAdded(true);
    }
  }, [watch()]);

  useEffect(() => {
    if (id) {
      if (quizChapter?.data?.[0]) {
        let values = _.pick(quizChapter?.data?.[0], [
          "quiz_number",
          "subject_id",
          "class_id",
          "board_id",
          "chapters_id",
        ]);

        // Parse the chapters_id JSON string to array
        const chaptersArray = JSON.parse(values.chapters_id || "[]");

        values = {
          ...values,
          quiz_number: values.quiz_number,
          subject_id: values.subject_id,
          board_id: values.board_id,
          class_id: values.class_id,
          chapters_id: chaptersArray, // Set as array
        };

        Object.entries(values).forEach(([key, val]) => {
          setValue(key, val);
        });

        // Set the selected chapters for the Select component
        if (chaptersArray.length > 0) {
          setValue(
            "chapters_id",
            chaptersArray.map((id) => Number(id))
          );
        }
      }
    } else {
      reset();
    }
    setRender(true);
  }, [id, quizChapter, reset, setValue]);

  const generateOptions = () => {
    const options = [];
    for (let i = 1; i <= num_of_quiz; i++) {
      options.push({
        value: i,
        label: `Quiz ${i}`,
        disabled: existingQuizNumbers.includes(i.toString()),
      });
    }
    return options;
  };

  useEffect(() => {
    if (quizChapter?.data?.[0]?.chapters_id) {
      const selectedChapterIds = JSON.parse(quizChapter.data[0].chapters_id);
      setValue("chapters_id", selectedChapterIds);
    }
  }, [quizChapter, setValue]);

  // Function to remove a chapter
  const removeChapter = (chapterId) => {
    const currentChapters = watch("chapters_id") || [];
    const updatedChapters = currentChapters.filter((id) => id !== chapterId);
    setValue("chapters_id", updatedChapters);
  };

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="header">
          <h2>{id ? "Edit" : "Create"} Quiz Chapters</h2>
        </div>

        <div className="body">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row clearfix">
              <FormSelect
                control={control}
                error={formState?.errors?.quiz_number}
                item={{
                  name: "quiz_number",
                  label: "Quiz Number",
                  options: generateOptions(),
                  disabled: id ? true : false,
                  rules,
                }}
              />

              {/* Chapters Select with Tags */}
              <div className="col-12 mb-4">
                <div className="form-group">
                  <label>Chapters</label>

                  {/* Selected chapters as tags above the field */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      marginBottom: "8px",
                      minHeight: "32px",
                    }}
                  >
                    {(watch("chapters_id") || []).map((chapterId) => {
                      const chapter = chapters?.data?.find(
                        (c) => c.id === chapterId
                      );
                      return chapter ? (
                        <div
                          key={chapterId}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: "#e0e0e0",
                            padding: "4px 8px",
                            borderRadius: "4px",
                          }}
                        >
                          <span>{chapter.name}</span>
                          {id && ( // Only show remove button when editing
                            <button
                              type="button"
                              onClick={() => removeChapter(chapterId)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                marginLeft: "4px",
                                color: "#666",
                                fontSize: "16px",
                                lineHeight: "1",
                              }}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ) : null;
                    })}
                  </div>

                  {/* Select field (shows only unselected chapters) */}
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">
                        <i className="fa fa-book"></i>
                      </span>
                    </div>
                    <Select
                      className="form-control"
                      style={{
                        border: "none",
                        boxShadow: "none",
                        padding: "0.375rem 0.75rem",
                      }}
                      placeholder="Select Chapters"
                      options={
                        chapters?.data
                          ?.filter(
                            (chapter) =>
                              !watch("chapters_id")?.includes(chapter.id)
                          )
                          ?.map((ele) => ({
                            value: ele.id,
                            label: ele.name,
                          })) || []
                      }
                      multi={true}
                      searchable={true}
                      dropdownPosition="auto"
                      dropdownHeight="300px"
                      clearable={true}
                      noDataRenderer={() => (
                        <div className="p-2 text-muted">No chapters found</div>
                      )}
                      onChange={(values) => {
                        const currentChapters = watch("chapters_id") || [];
                        const newChapters = values.map((v) => v.value);
                        setValue("chapters_id", [
                          ...currentChapters,
                          ...newChapters,
                        ]);
                      }}
                      value={[]} // Keep empty as we manage selection separately
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Add a close button to the form */}
            <div className="row clearfix">
              <div className="col-12 text-right">
                <button
                  type="button"
                  className="btn btn-secondary mr-2"
                  onClick={() => {
                    reset(); // Reset all form fields to their default values
                    if (id) {
                      setEditId(""); // Clear edit mode if editing
                      setAddUpdate(true);
                    }
                  }}
                >
                  Reset
                </button>
                <button
                  type="submit"
                  disabled={formState.isSubmitting}
                  className="btn btn-primary"
                >
                  {id ? "Update" : "Create"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateUpdateQuizChapters;
